<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { array, date, object, string } from 'yup';

const { tenants, tenant, semEvent } = defineProps({
    tenants: {
        required: false,
        type: Array,
    },
    tenant: {
        required: false,
        type: Object,
    },
    semEvent: {
        required: false,
        type: Object,
    },
});

const { route } = useRoute();

const form = reactive({
    tenants: [],
    name: semEvent ? semEvent.name : '',
    startedAt: semEvent ? semEvent.startedAt : null,
    endedAt: semEvent ? semEvent.endedAt : null,
    description: semEvent ? semEvent.description : null,
});

const isSubmitting = ref(false);

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    startedAt: date().nullable(),
    endedAt: date().nullable(),
    description: string().nullable(),
    ...(tenant
        ? {}
        : {
              tenants: array().min(1, 'Ce champ est obligatoire.'),
          }),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            tenant
                ? route('front.projectManagement.sem.postEvent', {
                      tenant: tenant.id,
                      ...(semEvent ? { semEventId: semEvent.id } : {}),
                  })
                : route('front.projectManagement.sem.postCreateEvent'),
            {
                name: form.name,
                startedAt: form.startedAt,
                endedAt: form.endedAt,
                description: form.description,
                ...(tenant
                    ? {}
                    : { tenantIds: form.tenants.map((tenant) => tenant.id) }),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.sem.events',
                    tenant ? { tenant: tenant.id } : {},
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route(
                    'front.projectManagement.sem.events',
                    tenant ? { tenant: tenant.id } : {},
                ),
            );
        });
}

function toggleEcommerceTenants() {
    form.tenants = tenants.filter(
        (tenant) => tenant.type.value === 'e-commerce',
    );
}

function toggleShowcaseTenants() {
    form.tenants = tenants.filter((tenant) => tenant.type.value === 'showcase');
}

function toggleEmptyTenants() {
    form.tenants = [];
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent v-if="tenants">
                <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
                    <ComboboxField
                        v-model="form.tenants"
                        :options="tenants"
                        label="Clients"
                        field-name="tenants"
                        field-id="tenants"
                        filteredOptionKey="name"
                        multiple
                        required
                    >
                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ComboboxField>

                    <div class="col-span-4 flex items-start gap-2">
                        <ActionButton :callback="toggleEcommerceTenants">
                            E-commerce
                        </ActionButton>

                        <ActionButton :callback="toggleShowcaseTenants">
                            Vitrine
                        </ActionButton>

                        <ActionButton :callback="toggleEmptyTenants">
                            Effacer
                        </ActionButton>
                    </div>
                </div>
            </FormContent>

            <FormContent>
                <InputField
                    v-model="form.name"
                    label="Nom"
                    field-name="name"
                    field-id="name"
                    type="text"
                    required
                />
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.startedAt"
                        label="Date de début"
                        field-name="startedAt"
                        field-id="startedAt"
                        type="date"
                    />

                    <InputField
                        v-model="form.endedAt"
                        label="Date de fin"
                        field-name="endedAt"
                        field-id="endedAt"
                        type="date"
                    />
                </FormGrid>

                <hr class="my-4 h-0.25 w-full bg-gray-200" />

                <EditorField
                    v-model="form.description"
                    label="Description"
                    field-name="description"
                    field-id="description"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="semEvent"
                    :url="
                        route('front.projectManagement.sem.deleteEvent', {
                            tenant: tenant.id,
                            semEventId: semEvent.id,
                        })
                    "
                    :callback="
                        () => {
                            route(
                                'front.projectManagement.sem.events',
                                tenant ? { tenant: tenant.id } : {},
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
